export interface ProfileCompletionResult {
  percentage: number;
  missingFields: string[];
  nextSteps: string[];
  completedSections: string[];
  incompleteSections: string[];
  weightedScore: number;
  missingSections: string[];
  isComplete: boolean;
}

interface ProfileData {
  full_name?: string;
  phone_number?: string;
  location?: string;
  bio?: string;
  current_job_title?: string;
  job_status_type?: string;
  career_stage?: string;
  education_level?: string;
  field_of_study?: string;
  career_interests?: string[];
  career_goals_list?: string[];
  skills_to_develop?: string[];
  avatar_url?: string;
}

// Field weights for completion calculation
// SINGLE SOURCE OF TRUTH - unified criteria
const FIELD_WEIGHTS = {
  // Critical fields (20 points each) - Required for 50%+ completion
  full_name: 20,
  phone_number: 20,
  career_stage: 20,
  education_level: 20,
  career_interests: 20, // At least 1 interest

  // Optional fields (10 points each) - Enhanced profile
  job_status_type: 10,
  current_job_title: 10,
  location: 10,
  bio: 10,
  field_of_study: 10,
  career_goals_list: 10,
  skills_to_develop: 10,
  avatar_url: 10,
};

export const calculateProfileCompletion = (profile: ProfileData): ProfileCompletionResult => {
  let totalPoints = 0;
  let earnedPoints = 0;
  const missingFields: string[] = [];
  const completedSections: string[] = [];
  const incompleteSections: string[] = [];

  // Calculate total possible points
  Object.values(FIELD_WEIGHTS).forEach(weight => {
    totalPoints += weight;
  });

  // Check each field and award points
  Object.entries(FIELD_WEIGHTS).forEach(([field, weight]) => {
    const value = profile[field as keyof ProfileData];
    
    let isComplete = false;
    if (Array.isArray(value)) {
      isComplete = value.length > 0;
    } else if (typeof value === 'string') {
      isComplete = value.trim().length > 0;
    }

    if (isComplete) {
      earnedPoints += weight;
      
      // Track completed sections
      if (field === 'full_name' || field === 'phone_number' || field === 'location') {
        if (!completedSections.includes('Basic Info')) completedSections.push('Basic Info');
      } else if (field === 'job_status_type' || field === 'career_stage' || field === 'current_job_title') {
        if (!completedSections.includes('Career Details')) completedSections.push('Career Details');
      } else if (field === 'education_level' || field === 'field_of_study') {
        if (!completedSections.includes('Education')) completedSections.push('Education');
      } else if (field === 'career_interests' || field === 'career_goals_list' || field === 'skills_to_develop') {
        if (!completedSections.includes('Career Goals')) completedSections.push('Career Goals');
      }
    } else {
      missingFields.push(field);
      
      // Track incomplete sections
      if (field === 'full_name' || field === 'phone_number') {
        if (!incompleteSections.includes('Basic Info')) incompleteSections.push('Basic Info');
      } else if (field === 'career_stage' || field === 'education_level') {
        if (!incompleteSections.includes('Career Foundation')) incompleteSections.push('Career Foundation');
      } else if (field === 'career_interests') {
        if (!incompleteSections.includes('Career Interests')) incompleteSections.push('Career Interests');
      }
    }
  });

  const percentage = Math.round((earnedPoints / totalPoints) * 100);

  // Generate next steps based on missing critical fields
  const nextSteps: string[] = [];
  if (!profile.full_name?.trim()) {
    nextSteps.push('Add your full name');
  }
  if (!profile.phone_number?.trim()) {
    nextSteps.push('Add your phone number');
  }
  if (!profile.career_stage) {
    nextSteps.push('Select your career stage');
  }
  if (!profile.education_level) {
    nextSteps.push('Add your education level');
  }
  if (!profile.career_interests || profile.career_interests.length === 0) {
    nextSteps.push('Choose at least one career interest');
  }
  
  // Add optional next steps if critical fields are complete
  if (nextSteps.length === 0) {
    if (!profile.bio?.trim()) {
      nextSteps.push('Add a professional bio');
    }
    if (!profile.career_goals_list || profile.career_goals_list.length === 0) {
      nextSteps.push('Set your career goals');
    }
    if (!profile.skills_to_develop || profile.skills_to_develop.length === 0) {
      nextSteps.push('Add skills you want to develop');
    }
  }

  return {
    percentage,
    missingFields,
    nextSteps: nextSteps.slice(0, 3), // Show max 3 next steps
    completedSections,
    incompleteSections,
    weightedScore: percentage, // Same as percentage in unified system
    missingSections: incompleteSections, // Alias for compatibility
    isComplete: missingFields.length === 0
  };
};

/**
 * Get user-friendly field name for display
 */
export const getFieldDisplayName = (field: string): string => {
  const displayNames: Record<string, string> = {
    full_name: 'Full Name',
    phone_number: 'Phone Number',
    location: 'Location',
    bio: 'Bio',
    current_job_title: 'Job Title',
    job_status_type: 'Current Status',
    career_stage: 'Career Stage',
    education_level: 'Education Level',
    field_of_study: 'Field of Study',
    career_interests: 'Career Interests',
    career_goals_list: 'Career Goals',
    skills_to_develop: 'Skills to Develop',
    avatar_url: 'Profile Photo'
  };
  
  return displayNames[field] || field;
};
